// src/components/droitsClassesMatieres.js
// Les droits d'écriture de l'écran Classes & matières, lus au serveur.
//
// La seule source est GET /api/classes/droits : la même fonction y calcule la
// garde des routes et la réponse faite à l'écran, ils ne peuvent pas diverger.
// On ne les déduit JAMAIS de usePermissions ni de detailedPermissions : une clé
// absente du catalogue de l'école n'est dans la liste de personne, alors que le
// serveur la laisse passer par repli sur la porte du module. C'est le cas de
// CLASSES.ASSOCIATIONS.MANAGE tant que sa migration attend
// (middlewares/gardeAssociationsClasses.js) : l'écran cacherait l'association à
// ceux qui l'ont aujourd'hui. Le jour où la clé entre au catalogue, la route
// répond autrement et l'écran suit tout seul.
//
// Fermé par défaut : tant que la première réponse n'est pas arrivée, ou si elle
// échoue, les droits valent DROITS_FERMES (cette référence-là, pas une copie) et
// aucun geste d'écriture n'est rendu. `droitsVerifies` s'appuie sur cette
// référence pour taire les bandeaux « il faut la permission » quand les droits
// n'ont pas pu être lus : ce n'est alors pas une permission qui manque.

import { useCallback, useEffect, useRef, useState } from 'react';
import { getAuthHeaders, sortirPourAccesSuspendu } from '../services/api';

export const DROITS_FERMES = Object.freeze({
  creer: false,
  modifier: false,
  supprimer: false,
  reordonner: false,
  seuils: false,
  associer: false,
  matieres: Object.freeze({ creer: false, modifier: false, supprimer: false })
});

const NON_VERIFIES = "Vos droits sur les classes n'ont pas pu être vérifiés.";

/** Libellés des clés, tels que l'écran Rôles & Permissions les décrit. */
export const LIBELLES = Object.freeze({
  'CLASSES.CREATE': 'Créer une classe',
  'CLASSES.UPDATE': 'Modifier une classe',
  'CLASSES.DELETE': 'Supprimer une classe',
  'CLASSES.REORDER': 'Réordonner les classes',
  'CLASSES.SEUILS.UPDATE': 'Mettre à jour les seuils (global/individuel)',
  'CLASSES.MATIERES.CREATE': 'Créer une matière',
  'CLASSES.MATIERES.UPDATE': 'Modifier une matière',
  'CLASSES.MATIERES.DELETE': 'Supprimer une matière',
  'CLASSES.ASSOCIATIONS.MANAGE': 'Associer les matières aux classes'
});

const vrai = (valeur) => valeur === true;

/** Seul un `true` explicite ouvre un geste ; toute autre valeur le ferme. */
export function normaliserDroits(data) {
  const matieres = data?.matieres || {};
  return {
    creer: vrai(data?.creer),
    modifier: vrai(data?.modifier),
    supprimer: vrai(data?.supprimer),
    reordonner: vrai(data?.reordonner),
    seuils: vrai(data?.seuils),
    associer: vrai(data?.associer),
    matieres: {
      creer: vrai(matieres.creer),
      modifier: vrai(matieres.modifier),
      supprimer: vrai(matieres.supprimer)
    }
  };
}

/** Vrai quand les droits viennent d'une réponse du serveur (même tous fermés). */
export const droitsVerifies = (droits) => Boolean(droits) && droits !== DROITS_FERMES;

async function lireCorps(reponse) {
  let corps = null;
  try {
    corps = await reponse.json();
  } catch {
    corps = null;
  }
  if (reponse.status === 401 && corps?.code === 'ACCESS_SUSPENDED') {
    sortirPourAccesSuspendu(corps.message);
  }
  return corps;
}

// Même lecture que le client commun : un refus de validation porte le vrai
// motif dans `errors`, pas dans son message générique.
function messageDuCorps(corps, reponse) {
  const erreurs = Array.isArray(corps?.errors) ? corps.errors : [];
  if (erreurs.length > 0 && (!corps?.message || /validation/i.test(corps.message))) {
    return erreurs.map((e) => e?.msg || e?.message || String(e)).filter(Boolean).join(' ');
  }
  if (corps?.message) return corps.message;
  if (typeof corps?.error === 'string' && corps.error) return corps.error;
  return reponse.statusText || 'Une erreur est survenue';
}

/** Les droits de l'agent connecté sur les classes et les matières. */
export async function lireDroitsClasses() {
  let reponse;
  try {
    reponse = await fetch('/api/classes/droits', { headers: getAuthHeaders() });
  } catch {
    throw new Error('Connexion au serveur impossible.');
  }
  const corps = await lireCorps(reponse);
  if (!reponse.ok || corps?.success === false) {
    const erreur = new Error(corps?.message || NON_VERIFIES);
    erreur.status = reponse.status;
    erreur.code = corps?.code;
    throw erreur;
  }
  return corps?.data;
}

/**
 * Marque une classe sans matière (le serveur retire ses associations dans la
 * même transaction) ou lui rend ses matières.
 *
 * Même forme que le client commun : un succès rend le corps ; un refus métier
 * (400, 403, 409) rend `{ success: false, message, code, status, data }` ; le
 * reste lève une erreur.
 */
export async function marquerClasseSansMatiere(classeId, sansMatiere) {
  let reponse;
  try {
    reponse = await fetch(`/api/classes/${classeId}/sans-matiere`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ sans_matiere: Boolean(sansMatiere) })
    });
  } catch {
    throw new Error('Connexion au serveur impossible.');
  }
  const corps = await lireCorps(reponse);
  if (reponse.ok) return corps;

  const message = messageDuCorps(corps, reponse);
  if (reponse.status === 400 || reponse.status === 403 || reponse.status === 409) {
    return {
      success: false,
      message,
      error: message,
      code: corps?.code,
      status: reponse.status,
      errors: corps?.errors,
      data: corps
    };
  }
  const erreur = new Error(message);
  erreur.code = corps?.code;
  erreur.status = reponse.status;
  erreur.data = corps;
  throw erreur;
}

/** Un refus de permission du serveur (porte du module ou clé d'action). */
export const estRefusDeDroit = (r) => (
  r?.status === 403 && ['INSUFFICIENT_PERMISSIONS', 'MODULE_ACCESS_DENIED'].includes(r?.code)
);

/** Le refus dit en clair, à la place du message technique du serveur. */
export function messageRefus(r) {
  const cle = r?.data?.requiredPermission;
  if (cle === 'CLASSE_ET_MATIERES.ACCESS' || r?.code === 'MODULE_ACCESS_DENIED') {
    return "Vous n'avez pas accès au module Classes & matières.";
  }
  if (cle && LIBELLES[cle]) {
    return `Action refusée : il faut la permission « ${LIBELLES[cle]} ».`;
  }
  return "Action refusée : vous n'avez pas la permission nécessaire.";
}

/** « a », « a et b », « a, b et c » ; la conjonction est « et » ou « ni ». */
export function enumerer(elements, conjonction = 'et') {
  if (elements.length <= 1) return elements.join('');
  return `${elements.slice(0, -1).join(', ')} ${conjonction} ${elements[elements.length - 1]}`;
}

/** Le libellé d'une clé entre guillemets : « Créer une classe ». */
export const citer = (cle) => `« ${LIBELLES[cle] || cle} »`;

/** « la permission « X » » ou « les permissions « X » et « Y » ». */
export function permissionsRequises(cles) {
  return `${cles.length > 1 ? 'les permissions' : 'la permission'} ${enumerer(cles.map(citer), 'et')}`;
}

// --- Bandeaux « vous pouvez consulter, mais pas… » ---------------------------
// Un bandeau par zone, composé des seuls gestes manquants, dans l'ordre de la
// zone. Chaque fonction rend le texte, ou null quand rien ne manque.

const GESTES_SECTIONS = [
  { droit: 'creer', geste: 'les créer', cle: 'CLASSES.CREATE' },
  { droit: 'modifier', geste: 'les modifier', cle: 'CLASSES.UPDATE' },
  { droit: 'supprimer', geste: 'les supprimer', cle: 'CLASSES.DELETE' },
  { droit: 'reordonner', geste: 'les réordonner', cle: 'CLASSES.REORDER' }
];

const GESTES_ANNEXES = [
  { droit: 'creer', geste: 'en créer', cle: 'CLASSES.CREATE' },
  { droit: 'supprimer', geste: 'les supprimer', cle: 'CLASSES.DELETE' }
];

/** Liste des sections de classes. */
export function texteBandeauSections(droits) {
  const manquants = GESTES_SECTIONS.filter(({ droit }) => !droits[droit]);
  if (manquants.length === 0) return null;
  return `Vous pouvez consulter les classes, mais pas ${enumerer(manquants.map((g) => g.geste), 'ni')} : il faut, `
    + `dans le module Classes & matières, ${permissionsRequises(manquants.map((g) => g.cle))}.`;
}

/** Onglet Annexes du détail d'une classe. */
export function texteBandeauAnnexes(droits) {
  const manquants = GESTES_ANNEXES.filter(({ droit }) => !droits[droit]);
  if (manquants.length === 0) return null;
  return `Vous pouvez consulter les classes annexes, mais pas ${enumerer(manquants.map((g) => g.geste), 'ni')} : `
    + `il faut ${permissionsRequises(manquants.map((g) => g.cle))}.`;
}

/** Onglet Seuils du détail d'une classe. */
export function texteBandeauSeuil(droits) {
  if (droits.seuils) return null;
  return 'Vous pouvez consulter le seuil de cette classe, mais pas le modifier : '
    + `il faut ${permissionsRequises(['CLASSES.SEUILS.UPDATE'])}.`;
}

/** Onglet Matières du détail d'une classe. */
export function texteBandeauAssociationsClasse(droits) {
  if (droits.associer) return null;
  return 'Vous pouvez consulter les matières de cette classe, mais pas les associer, régler leurs coefficients '
    + `ni marquer la classe sans matière : il faut ${permissionsRequises(['CLASSES.ASSOCIATIONS.MANAGE'])}.`;
}

/** Une matière se crée avec au moins une classe, donc avec ses associations. */
export const peutCreerMatieres = (droits) => droits.matieres.creer && droits.associer;

/** Liste des matières. */
export function texteBandeauMatieres(droits) {
  const creation = peutCreerMatieres(droits);
  const gestes = [];
  const cles = [];
  if (!creation) gestes.push('en créer');
  if (!droits.matieres.modifier) { gestes.push('les modifier'); cles.push('CLASSES.MATIERES.UPDATE'); }
  if (!droits.matieres.supprimer) { gestes.push('les supprimer'); cles.push('CLASSES.MATIERES.DELETE'); }
  if (!droits.associer) { gestes.push('changer leurs classes'); cles.push('CLASSES.ASSOCIATIONS.MANAGE'); }
  if (gestes.length === 0) return null;

  const raisons = [];
  if (cles.length > 0) raisons.push(`il faut ${permissionsRequises(cles)}`);
  if (!creation) {
    raisons.push(`créer une matière demande ${citer('CLASSES.MATIERES.CREATE')} et `
      + `${citer('CLASSES.ASSOCIATIONS.MANAGE')}, car une matière se crée avec au moins une classe`);
  }
  return `Vous pouvez consulter les matières, mais pas ${enumerer(gestes, 'ni')} : ${raisons.join(' ; ')}.`;
}

/**
 * `{ droits, chargement, erreur, recharger }`.
 *
 * Un rechargement garde les droits déjà lus jusqu'à la réponse : changer
 * d'onglet ne fait pas clignoter les boutons. Un numéro de demande écarte les
 * réponses arrivées en retard ; un échec referme tout.
 */
export function useDroitsClassesMatieres() {
  const [etat, setEtat] = useState({ droits: DROITS_FERMES, chargement: true, erreur: null });
  const demandeRef = useRef(0);

  const recharger = useCallback(async () => {
    demandeRef.current += 1;
    const numero = demandeRef.current;
    setEtat((precedent) => (precedent.chargement ? precedent : { ...precedent, chargement: true }));
    try {
      const data = await lireDroitsClasses();
      if (demandeRef.current !== numero) return;
      setEtat({ droits: normaliserDroits(data), chargement: false, erreur: null });
    } catch (erreur) {
      if (demandeRef.current !== numero) return;
      setEtat({ droits: DROITS_FERMES, chargement: false, erreur: erreur?.message || NON_VERIFIES });
    }
  }, []);

  useEffect(() => {
    recharger();
  }, [recharger]);

  // Au démontage, une réponse encore en route ne touche plus l'état.
  useEffect(() => () => {
    demandeRef.current += 1;
  }, []);

  return { ...etat, recharger };
}
